<template>
  <section class="tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-gray-200">
    <WdsStack direction-column gap-md class="tw-bg-true-gray-50 tw-px-6 tw-py-4">
      <div class="tw-flex tw-items-baseline tw-justify-between tw-gap-4">
        <div class="tw-flex tw-min-w-0 tw-items-baseline tw-gap-2">
          <WdsHeading type="title3">{{ checklist.template.name }}</WdsHeading>
          <WdsText v-if="checklist.template.ownerName" secondary>
            {{ $t('Set by :owner', { owner: checklist.template.ownerName }) }}
          </WdsText>
        </div>
        <WdsText secondary class="tw-flex-none tw-tabular-nums">
          {{ $t(':done of :total done', { done: checklist.checkCounts.done, total: checklist.checkCounts.total }) }}
        </WdsText>
      </div>

      <ChecklistProgress
        :done="checklist.checkCounts.done"
        :total="checklist.checkCounts.total"
        :blocking-pending="checklist.checkCounts.blockingPending"
        :has-blocking-checks
      />
    </WdsStack>

    <ul class="tw-m-0 tw-list-none tw-p-0">
      <ChecklistCheckRow
        v-for="check in openChecks"
        :key="check.id"
        :check="check"
        :can-decide="checklist.viewerCanDecide"
      />
    </ul>

    <template v-if="finishedChecks.length > 0">
      <button
        type="button"
        data-test="checklist-finished-toggle"
        class="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-1.5 tw-border-0 tw-border-t tw-border-solid tw-border-gray-100 tw-bg-true-gray-50 tw-px-6 tw-py-3 tw-text-left hover:tw-bg-true-gray-100"
        :aria-expanded="showFinished"
        @click="showFinished = !showFinished"
      >
        <WdsIcon :name="showFinished ? 'chevron-down' : 'chevron-right'" :size="16" />
        <WdsText secondary>{{ $t(':count finished', { count: finishedChecks.length }) }}</WdsText>
      </button>

      <ul v-show="showFinished" class="tw-m-0 tw-list-none tw-p-0">
        <ChecklistFinishedRow
          v-for="check in finishedChecks"
          :key="check.id"
          :check="check"
          :can-decide="checklist.viewerCanDecide"
        />
      </ul>
    </template>
  </section>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { WdsHeading, WdsIcon, WdsStack, WdsText } from '@worksome/wds-components'
import ChecklistCheckRow from './ChecklistCheckRow.vue'
import ChecklistFinishedRow from './ChecklistFinishedRow.vue'
import ChecklistProgress from './ChecklistProgress.vue'
import { ChecklistCardFragment } from './fragments'
import { FragmentType, useFragment } from '@/gql'
import { ChecklistCheckState } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

const props = defineProps<{
  checklist: FragmentType<typeof ChecklistCardFragment>
}>()

const checklist = computed(() => useFragment(ChecklistCardFragment, props.checklist))
const showFinished = ref(true)

const openChecks = computed(() => checklist.value.checks.filter((check) => check.state === ChecklistCheckState.Pending))
const finishedChecks = computed(() =>
  checklist.value.checks.filter((check) => check.state !== ChecklistCheckState.Pending),
)
const hasBlockingChecks = computed(() => checklist.value.checks.some((check) => check.blocking))
</script>
